import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { 
    User, 
    Mail, 
    Crown, 
    Check, 
    Loader2, 
    Plane,
    Globe,
    MapPin,
    Zap,
    Settings,
    ExternalLink,
    Route,
    Crosshair
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProfilePage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, isPremium, selectedCountry, subscriptionType, refreshUser, isAuthenticated, loading } = useAuth();
    
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingPayment, setIsCheckingPayment] = useState(false);
    const [isLoadingPortal, setIsLoadingPortal] = useState(false);
    const [isSyncingStatus, setIsSyncingStatus] = useState(false);

    // Check for payment success on page load
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            checkPaymentStatus(sessionId);
        }
    }, [searchParams]);

    // Redirect if not authenticated (wait for loading to complete first)
    useEffect(() => {
        if (!loading && !isAuthenticated) {
            navigate('/login');
        }
    }, [isAuthenticated, loading, navigate]);

    const checkPaymentStatus = async (sessionId) => {
        setIsCheckingPayment(true);
        let attempts = 0;
        const maxAttempts = 5;
        const pollInterval = 2000;

        const poll = async () => {
            try {
                const response = await axios.get(`${API}/payments/status/${sessionId}`);
                
                if (response.data.payment_status === 'paid') {
                    await refreshUser();
                    toast.success('Payment successful! You are now a Premium member with worldwide access.');
                    // Remove session_id from URL
                    navigate('/profile', { replace: true });
                    return;
                } else if (response.data.status === 'expired') {
                    toast.error('Payment session expired. Please try again.');
                    navigate('/profile', { replace: true });
                    return;
                }

                // Continue polling if still pending
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(poll, pollInterval);
                } else {
                    toast.info('Payment is still processing. Please refresh the page in a moment.');
                    navigate('/profile', { replace: true });
                }
            } catch (error) {
                console.error('Error checking payment:', error);
                toast.error('Error checking payment status');
                navigate('/profile', { replace: true });
            }
        };

        await poll();
        setIsCheckingPayment(false);
    };

    const handleUpgrade = async () => {
        setIsLoading(true);
        
        try {
            const response = await axios.post(`${API}/payments/checkout`, {
                origin_url: window.location.origin
            });
            
            // Redirect to Stripe checkout
            window.location.href = response.data.url;
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error('Failed to start checkout. Please try again.');
            setIsLoading(false);
        }
    };

    const handleManageSubscription = async () => {
        setIsLoadingPortal(true);
        
        try {
            const response = await axios.post(`${API}/payments/portal`);
            // Open Stripe Customer Portal
            window.location.href = response.data.url;
        } catch (error) {
            console.error('Portal error:', error);
            if (error.response?.status === 404) {
                toast.error('No active subscription found.');
            } else {
                toast.error('Failed to open subscription management. Please try again.');
            }
            setIsLoadingPortal(false);
        }
    };

    const handleSyncStatus = async () => {
        setIsSyncingStatus(true);
        
        try {
            const response = await axios.get(`${API}/payments/sync-status`);
            
            if (response.data.status === 'upgraded') {
                await refreshUser();
                toast.success('Premium status restored! Enjoy your worldwide access.');
            } else {
                toast.info('No completed payment found for your account.');
            }
        } catch (error) {
            console.error('Sync error:', error);
            toast.error('Failed to sync status. Please contact support.');
        } finally {
            setIsSyncingStatus(false);
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-[#071D35] flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-[#f07050] animate-spin" />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-[#071D35] flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-[#f07050] animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#071D35] pt-20 pb-12 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Payment Processing Overlay */}
                {isCheckingPayment && (
                    <div className="fixed inset-0 bg-[#071D35]/80 z-50 flex items-center justify-center">
                        <Card className="bg-[#1e293b] border-[#334155]">
                            <CardContent className="p-8 text-center">
                                <Loader2 className="h-12 w-12 text-[#f07050] animate-spin mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-white mb-2">
                                    Processing Payment
                                </h3>
                                <p className="text-[#94a3b8]">
                                    Please wait while we confirm your subscription...
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Profile Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#1e293b] border-2 border-[#334155] mb-4">
                        <User className="h-10 w-10 text-[#f07050]" />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">
                        {user.username}
                    </h1>
                    <div className="flex items-center justify-center gap-2">
                        <Mail className="h-4 w-4 text-[#64748b]" />
                        <span className="text-[#94a3b8]">{user.email}</span>
                    </div>
                    {isPremium ? (
                        <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-[#f07050]/20 border border-[#f07050]/30">
                            <Crown className="h-4 w-4 text-[#f07050]" />
                            <span className="text-[#f07050] font-medium">Premium - Worldwide Access</span>
                        </div>
                    ) : (
                        <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-[#334155]/50 border border-[#334155]">
                            <MapPin className="h-4 w-4 text-[#94a3b8]" />
                            <span className="text-[#94a3b8] font-medium">Free - {selectedCountry || 'No country selected'}</span>
                        </div>
                    )}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    {/* Account Status Card */}
                    <Card className="bg-[#1e293b]/50 backdrop-blur-md border-[#334155]">
                        <CardHeader>
                            <CardTitle className="text-xl text-white">Account Status</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between p-3 rounded-lg bg-[#071D35]/50">
                                <span className="text-[#94a3b8]">Plan</span>
                                <span className={`font-bold ${isPremium ? 'text-[#f07050]' : 'text-white'}`}>
                                    {isPremium ? 'Premium ($4.99/mo)' : 'Free'}
                                </span>
                            </div>

                            <div className="flex items-center justify-between p-3 rounded-lg bg-[#071D35]/50">
                                <span className="text-[#94a3b8]">Coverage</span>
                                <span className="text-white flex items-center gap-2">
                                    {isPremium ? (
                                        <>
                                            <Globe className="h-4 w-4 text-[#f07050]" />
                                            Worldwide
                                        </>
                                    ) : (
                                        <>
                                            <MapPin className="h-4 w-4 text-[#94a3b8]" />
                                            {selectedCountry || 'Not set'}
                                        </>
                                    )}
                                </span>
                            </div>

                            <div className="flex items-center justify-between p-3 rounded-lg bg-[#071D35]/50">
                                <span className="text-[#94a3b8]">Ads</span>
                                <span className={`font-medium ${isPremium ? 'text-green-500' : 'text-[#94a3b8]'}`}>
                                    {isPremium ? 'No Ads' : 'Ad-supported'}
                                </span>
                            </div>

                            <div className="flex items-center justify-between p-3 rounded-lg bg-[#071D35]/50">
                                <span className="text-[#94a3b8]">Member Since</span>
                                <span className="text-white">
                                    {new Date(user.created_at).toLocaleDateString()}
                                </span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Upgrade Card (for non-premium users) */}
                    {!isPremium ? (
                        <Card className="bg-gradient-to-br from-[#1e293b] to-[#334155] border-[#f07050]/30 relative overflow-hidden">
                            <div className="absolute top-0 right-0 px-4 py-1 bg-[#f07050] text-white text-xs font-bold">
                                UPGRADE
                            </div>
                            <CardHeader>
                                <CardTitle className="text-xl text-white flex items-center gap-2">
                                    <Crown className="h-5 w-5 text-[#f07050]" />
                                    Go Premium
                                </CardTitle>
                                <CardDescription className="text-[#94a3b8]">
                                    Unlock worldwide flight tracking
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="text-center mb-4">
                                    <span className="text-4xl font-bold text-white">$4.99</span>
                                    <span className="text-[#94a3b8] ml-2">/month</span>
                                </div>

                                <ul className="space-y-3">
                                    {[
                                        { icon: Globe, text: 'Track flights in ALL countries' },
                                        { icon: Route, text: 'Flight path trails - see where planes have been' },
                                        { icon: Crosshair, text: 'Follow flights in real-time' },
                                        { icon: Zap, text: 'No advertisements' },
                                        { icon: Check, text: 'Cancel anytime' }
                                    ].map((item, i) => (
                                        <li key={i} className="flex items-center gap-3 text-[#94a3b8]">
                                            <item.icon className="h-4 w-4 text-[#f07050]" />
                                            {item.text}
                                        </li>
                                    ))}
                                </ul>

                                <div className="p-3 rounded-lg bg-[#071D35]/50 border border-[#334155]">
                                    <p className="text-xs text-[#64748b] text-center">
                                        Currently tracking: <span className="text-white">{selectedCountry || 'Not set'}</span>
                                    </p>
                                </div>

                                <Button
                                    onClick={handleUpgrade}
                                    disabled={isLoading}
                                    className="w-full bg-[#f07050] hover:bg-[#e06040] text-white btn-primary-glow mt-4"
                                    data-testid="profile-upgrade-btn"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Redirecting to checkout...
                                        </>
                                    ) : (
                                        <>
                                            <Crown className="mr-2 h-4 w-4" />
                                            Subscribe Now - $4.99/mo
                                        </>
                                    )}
                                </Button>

                                <button
                                    onClick={handleSyncStatus}
                                    disabled={isSyncingStatus}
                                    className="w-full mt-2 text-xs text-[#64748b] hover:text-[#94a3b8] underline disabled:opacity-50"
                                    data-testid="sync-status-btn"
                                >
                                    {isSyncingStatus ? 'Checking...' : 'Already paid? Click here to restore access'}
                                </button>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="bg-[#1e293b]/50 backdrop-blur-md border-[#334155]">
                            <CardHeader>
                                <CardTitle className="text-xl text-white flex items-center gap-2">
                                    <Crown className="h-5 w-5 text-[#f07050]" />
                                    Premium Benefits
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="space-y-3">
                                    {[
                                        { icon: Globe, text: 'Worldwide flight tracking', active: true },
                                        { icon: Zap, text: 'Ad-free experience', active: true },
                                        { icon: Check, text: 'Monthly subscription - cancel anytime', active: true }
                                    ].map((item, i) => (
                                        <li key={i} className="flex items-center gap-3">
                                            <div className="p-1 rounded-full bg-green-500/20">
                                                <item.icon className="h-3 w-3 text-green-500" />
                                            </div>
                                            <span className="text-white">{item.text}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-6 p-4 rounded-lg bg-[#071D35]/50 text-center">
                                    <p className="text-[#94a3b8] text-sm">
                                        Thank you for being a Premium subscriber! 
                                        Enjoy worldwide flight tracking without ads.
                                    </p>
                                </div>

                                <Button
                                    onClick={handleManageSubscription}
                                    disabled={isLoadingPortal}
                                    variant="outline"
                                    className="w-full mt-4 border-[#334155] text-white hover:bg-[#334155]"
                                    data-testid="manage-subscription-btn"
                                >
                                    {isLoadingPortal ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Opening...
                                        </>
                                    ) : (
                                        <>
                                            <Settings className="mr-2 h-4 w-4" />
                                            Manage Subscription
                                            <ExternalLink className="ml-2 h-3 w-3" />
                                        </>
                                    )}
                                </Button>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Quick Actions */}
                <div className="mt-8">
                    <Button
                        onClick={() => navigate('/tracker')}
                        className="bg-[#f07050] hover:bg-[#e06040] text-white btn-primary-glow"
                        data-testid="profile-track-btn"
                    >
                        <Plane className="mr-2 h-4 w-4" />
                        Start Tracking Flights
                    </Button>
                </div>
            </div>
        </div>
    );
};
